import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import styled from "styled-components";
import {
  actionContainerStyles,
  actionLinkStyles,
  actionHeaderStyles,
} from "../common/ActionStyles";
import ResponsiveActions from "../common/ResponsiveActions";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import API from "../../../util/api";
import AdminResetPassword from "../Teachers/AdminResetPassword";
import ConfirmationModal from "../../Common/ConfirmationModal";
import {
  becomeUser,
  getSubscriptionDetail,
  setBecome,
  setBecomeOrigin,
} from "../../../sagas/actions";
import {
  adminTable,
  adminTableHeaderCell,
  adminTableDataCell,
} from "../common/AdminTableStyles";

const Container = styled.div`
  padding: 20px;
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
`;

const HeaderButton = styled.a`
  padding: 6px 12px;
  background: ${({ colors }) => colors.buttonBackground};
  color: white;
  border-radius: 4px;
  text-decoration: none;
  font-size: 14px;
  cursor: pointer;
  &:hover {
    background: ${({ colors }) => colors.buttonHoverBackground};
  }
`;

const ControlsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  align-items: center;

  label {
    font-size: 14px;
    margin-right: 8px;
    color: ${({ colors }) => colors.labelText};
  }

  select,
  input {
    padding: 5px 10px;
    font-size: 14px;
    border: 1px solid ${({ colors }) => colors.border};
    border-radius: 5px;
  }

  input {
    width: 400px;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  ${adminTable};
  font-size: 14px;

  thead th {
    ${adminTableHeaderCell};
    background-color: #fff;
    position: relative;

    .sort-icons {
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      display: flex;
      flex-direction: column;
      font-size: 10px;

      span {
        cursor: pointer;
        line-height: 10px;
        color: #9ca3af;
        display: block;
      }

      .active {
        color: #a855f7;
      }
    }
  }

  tbody td {
    ${adminTableDataCell};
    padding: 10px;
  }
`;

const InfoText = styled.div`
  margin: 10px 0;
  font-size: 12px;
  color: ${({ colors }) => colors.labelText};
`;

const Pagination = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;

  button {
    padding: 5px 10px;
    margin-left: 5px;
    border: 1px solid ${({ colors }) => colors.border};
    background-color: ${({ colors }) => colors.cardBackground};
    border-radius: 5px;
    cursor: pointer;

    &:disabled {
      background-color: ${({ colors }) => colors.disabledBackground};
      cursor: not-allowed;
    }

    &:hover:not(:disabled) {
      background-color: ${({ colors }) => colors.buttonBackground};
      color: white;
    }
  }
`;

const AdvancedForm = styled.form`
  max-width: 500px;
  margin: 0 auto;

  .input {
    margin-bottom: 1rem;

    label {
      display: block;
      margin-bottom: 0.25rem;
      color: ${({ colors }) => colors.labelText};
      font-size: 14px;
    }

    input {
      width: 100%;
      padding: 6px;
      border: 1px solid ${({ colors }) => colors.border};
      border-radius: 4px;
      font-size: 13px;
    }
  }

  .submit {
    margin-top: 1rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    input {
      padding: 8px 16px;
      background: ${({ colors }) => colors.buttonBackground};
      color: white;
      border: none;
      border-radius: 4px;
      font-size: 14px;
      cursor: pointer;
      &:hover {
        background: ${({ colors }) => colors.buttonHoverBackground};
      }
    }
  }
`;

const CancelLink = styled.a`
  display: inline-block;
  color: ${({ colors }) => colors.buttonBackground};
  text-decoration: none;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const DetailLayout = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: flex-start;
`;

const ActionsPanel = styled.div`
  ${actionContainerStyles};
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ colors }) => colors.border};
  box-shadow: 2px 5px 5px rgba(0, 0, 0, 0.05);

  @media (min-width: 768px) {
    flex: 0 0 260px;
  }
`;

const ActionsHeader = styled.div`
  ${actionHeaderStyles};
  border-bottom: 1px solid ${({ colors }) => colors.border};
`;

const ActionsBody = styled.div`
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ActionList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ActionButton = styled.button`
  ${actionLinkStyles};
  padding-left: 16px;
  background: none;
  border: none;
  text-align: left;
  color: #2c4960;
  transition: color 0.2s ease;

  &:hover:enabled {
    color: ${({ colors }) => colors.buttonBackground};
  }

  &:disabled {
    color: ${({ colors }) => colors.labelText};
    cursor: default;
    opacity: 0.65;
  }
`;

const DetailPanel = styled.div`
  flex: 1;
  min-width: 0;
  background: #ffffff;
  border: 1px solid ${({ colors }) => colors.border};
  border-radius: 6px;
  padding: 20px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  margin: 0 0 12px;
  color: ${({ colors }) => colors.labelText};
`;

const DetailSection = styled.div`
  margin-bottom: 24px;
`;

const DetailRowsWrapper = styled.div`
  border: 1px solid ${({ colors }) => colors.border};
  border-radius: 6px;
  overflow: hidden;
`;

const DetailRow = styled.div`
  display: grid;
  grid-template-columns: minmax(150px, 220px) 1fr;
  gap: 12px;
  padding: 12px 16px;
  background: ${({ $alt }) => ($alt ? "#f7f7f7" : "#ffffff")};
  border-bottom: 1px solid ${({ colors }) => colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  font-weight: 600;
  color: ${({ colors }) => colors.labelText};
`;

const DetailValue = styled.span`
  display: block;
  color: ${({ colors }) => colors.labelText};
  white-space: pre-wrap;

  .value-text {
    display: block;
  }

  .password-actions {
    margin-top: 8px;
  }
`;

const DetailMessage = styled.div`
  font-size: 14px;
  color: ${({ colors }) => colors.labelText};
`;

const AffiliationsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AffiliationsItem = styled.li`
  display: inline-block;
  padding: 6px 12px;
  border: 1px solid ${({ colors }) => colors.border};
  border-radius: 6px;
  background: #f9f9f9;
  color: ${({ colors }) => colors.labelText};
  font-size: 13px;
`;

const NameButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${({ colors }) => colors.buttonBackground};
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    color: ${({ colors }) => colors.darkBlue};
  }
`;

const ChangePasswordButton = styled.button`
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  background: ${({ colors }) => colors.buttonBackground};
  color: #ffffff;

  &:hover {
    background: ${({ colors }) => colors.buttonHoverBackground};
  }
`;

export default function UserSearchContent({ colors }) {
  // list | advanced
  const [view, setView] = useState("list");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // table params
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [orderBy, setOrderBy] = useState(null);
  const [order, setOrder] = useState(null);

  // simple search
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // advanced fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [advEmail, setAdvEmail] = useState("");
  const [city, setCity] = useState("");
  const [zip, setZip] = useState("");
  const [phone, setPhone] = useState("");

  // whether advanced filters are active
  const [advApplied, setAdvApplied] = useState(false);

  // results
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const detailRequestRef = useRef({ userId: null, inFlight: false });
  // fetch helper
  const fetchData = useCallback(async (payload) => {
    setLoading(true);
    try {
      const resp = await API.searchUsers(payload);
      setRows(resp.users || []);
      setTotal(Number(resp.total) || 0);
    } catch (err) {
      console.error("Search failed:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [search]);

  // simple‐search effect
  useEffect(() => {
    if (advApplied) return;
    fetchData({
      is_advance_search: 0,
      search: debouncedSearch,
      order_by: orderBy,
      order,
      page,
      items_per_page: itemsPerPage,
    });
  }, [
    debouncedSearch,
    orderBy,
    order,
    page,
    itemsPerPage,
    advApplied,
    fetchData,
  ]);

  // advanced‐search effect
  useEffect(() => {
    if (!advApplied) return;
    fetchData({
      is_advance_search: 1,
      search: "",
      order_by: orderBy,
      order,
      page,
      items_per_page: itemsPerPage,
      first_name: firstName,
      last_name: lastName,
      email: advEmail,
      city,
      zip,
      phone,
    });
  }, [
    advApplied,
    firstName,
    lastName,
    advEmail,
    city,
    zip,
    phone,
    orderBy,
    order,
    page,
    itemsPerPage,
    fetchData,
  ]);

  // toggle sort direction
  const handleSort = (key) => {
    const next = orderBy === key && order === "ASC" ? "DESC" : "ASC";
    setOrderBy(key);
    setOrder(next);
    setPage(0);
  };

  // advanced form submit
  const handleAdvancedSubmit = (e) => {
    e.preventDefault();
    setAdvApplied(true);
    setView("list");
    setPage(0);
  };

  // reset all filters back to simple mode
  const handleReset = () => {
    setAdvApplied(false);
    setFirstName("");
    setLastName("");
    setAdvEmail("");
    setCity("");
    setZip("");
    setPhone("");
    setSearch("");
    setDebouncedSearch("");
    setOrderBy(null);
    setOrder(null);
    setPage(0);
    setItemsPerPage(10);
    setSelectedUser(null);
    setUserDetail(null);
    setDetailError("");
    setDetailLoading(false);
    setShowPasswordReset(false);
  };

  const handleSelectUser = async (userRow) => {
    if (!userRow) return;
    const isSameRequestInFlight =
      detailRequestRef.current.inFlight &&
      detailRequestRef.current.userId === userRow.user_id;
    if (isSameRequestInFlight) return;

    detailRequestRef.current = { userId: userRow.user_id, inFlight: true };
    setSelectedUser(userRow);
    setUserDetail(null);
    setDetailError("");
    setDetailLoading(true);
    setView("list");
    setShowPasswordReset(false);

    try {
      const response = await API.getUserDetail({
        user_id: userRow.user_id,
        id: userRow.user_id,
      });
      setUserDetail(response || null);
    } catch (err) {
      console.error("User detail fetch failed:", err);
      setDetailError(
        err?.message || "Unable to load user detail. Please try again.",
      );
    } finally {
      setDetailLoading(false);
      if (detailRequestRef.current.userId === userRow.user_id) {
        detailRequestRef.current.inFlight = false;
      }
    }
  };

  const handleNewSearchFromDetail = () => {
    detailRequestRef.current = { userId: null, inFlight: false };
    setSelectedUser(null);
    setUserDetail(null);
    setDetailError("");
    setDetailLoading(false);
    setShowPasswordReset(false);
  };

  const formatBoolean = useCallback((value) => {
    if (value === undefined || value === null || value === "") return "--";
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (["y", "yes", "1", "true"].includes(normalized)) return "Yes";
      if (["n", "no", "0", "false"].includes(normalized)) return "No";
    }
    if (typeof value === "number") {
      return value === 0 ? "No" : "Yes";
    }
    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }
    return String(value);
  }, []);

  const membership = userDetail?.membership || null;

  const parseBooleanFlag = useCallback((value) => {
    if (value === undefined || value === null || value === "") return undefined;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (["1", "true", "y", "yes"].includes(normalized)) return true;
      if (["0", "false", "n", "no"].includes(normalized)) return false;
      return Boolean(normalized);
    }
    return Boolean(value);
  }, []);

  const isActive = useMemo(() => {
    const sources = [
      membership?.is_active,
      userDetail?.is_active,
      selectedUser?.is_active,
    ];
    for (const val of sources) {
      const parsed = parseBooleanFlag(val);
      if (parsed !== undefined) {
        return parsed;
      }
    }
    return true;
  }, [membership, userDetail, selectedUser, parseBooleanFlag]);

  const detailFieldConfig = useMemo(() => {
    const makeSourceList = (membership, detail, row) =>
      [membership, detail, row].filter(Boolean);

    const resolveName =
      (type) =>
      ({ membership, detail, row }) => {
        const sources = makeSourceList(membership, detail, row);
        for (const src of sources) {
          const direct =
            type === "first"
              ? (src?.first_name ?? src?.first)
              : (src?.last_name ?? src?.last);
          if (direct !== undefined && direct !== null && direct !== "") {
            return direct;
          }
        }

        for (const src of sources) {
          const full =
            src?.full_name ||
            src?.name ||
            [src?.first, src?.last].filter(Boolean).join(" ");
          if (full && full.trim()) {
            const parts = full.trim().split(/\s+/);
            if (parts.length) {
              return type === "first" ? parts[0] : parts[parts.length - 1];
            }
          }
        }

        return "";
      };

    return [
      { label: "First Name", resolve: resolveName("first") },
      { label: "Last Name", resolve: resolveName("last") },
      {
        label: "Address",
        resolve: ({ membership, detail, row }) => {
          const sources = makeSourceList(membership, detail, row);
          const getField = (...candidates) => {
            for (const src of sources) {
              for (const key of candidates) {
                const val = src?.[key];
                if (val !== undefined && val !== null && val !== "") {
                  return val;
                }
              }
            }
            return null;
          };

          const line1 = getField(
            "street",
            "address",
            "address1",
            "address_line1",
            "street1",
            "mailing_address",
          );
          const line2 = getField(
            "address2",
            "address_2",
            "street2",
            "address_line2",
            "street_line2",
          );
          const city = getField("city", "city_name");
          const state = getField("state", "state_code", "province", "provence");
          const zip = getField("zip", "postal_code", "zip_code", "zip4");

          const lines = [];
          if (line1) lines.push(line1);
          if (line2) lines.push(line2);

          const cityState = [city, state].filter(Boolean).join(", ");
          const cityStateZip = [cityState, zip].filter(Boolean).join(" ");
          if (cityStateZip) lines.push(cityStateZip.trim());

          return lines.join("\n");
        },
      },
      { label: "City", keys: ["city", "city_name"] },
      { label: "State", keys: ["state", "state_code", "province", "provence"] },
      { label: "Zip", keys: ["zip", "postal_code", "zip_code", "zip4"] },
      { label: "Phone", keys: ["phone", "mobile", "primary_phone"] },
      { label: "E-mail Address", keys: ["email"] },
      {
        label: "News Letter",
        resolve: ({ membership, row }) =>
          formatBoolean(
            membership?.news_letter ??
              membership?.newsletter ??
              row?.news_letter ??
              row?.newsletter,
          ),
      },
      {
        label: "I-Parent Board",
        resolve: ({ membership, row }) =>
          formatBoolean(
            membership?.iparent_board ??
              membership?.i_parent_board ??
              row?.iparent_board ??
              row?.i_parent_board,
          ),
      },
      { label: "Last Login", keys: ["last_login", "last_login_at"] },
      { label: "Login Count", keys: ["login_count", "logincount"] },
      { label: "Password", resolve: () => "••••••" },
    ];
  }, [formatBoolean]);

  const detailSources = useMemo(
    () => [membership, userDetail, selectedUser].filter(Boolean),
    [membership, userDetail, selectedUser],
  );

  const pickValue = (source, key) => {
    if (!source) return undefined;
    const value = source[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
    return undefined;
  };

  const readDetailValue = (config) => {
    if (config.resolve) {
      return config.resolve({
        membership,
        detail: userDetail,
        row: selectedUser,
      });
    }

    const keys = Array.isArray(config.keys) ? config.keys : [config.keys];
    for (const key of keys) {
      for (const source of detailSources) {
        const value = pickValue(source, key);
        if (value !== undefined) return value;
      }
    }
    return "";
  };

  const toDisplayValue = (value) => {
    if (value === undefined || value === null || value === "") {
      return "--";
    }
    if (Array.isArray(value)) {
      const joined = value.filter(Boolean).join(", ");
      return joined || "--";
    }
    return String(value);
  };

  const affiliations = useMemo(() => {
    if (!userDetail) return [];
    const collection = userDetail.affiliations || userDetail.affiliation || [];
    if (Array.isArray(collection)) {
      const mapped = collection
        .map((item) => {
          if (!item) return null;
          if (typeof item === "string") return item.trim() || null;
          if (typeof item === "object") {
            const name =
              item.org_name ||
              item.name ||
              item.title ||
              item.organization ||
              item.label;
            const city = item.org_city || item.city;
            const state = item.org_state || item.state;
            const zip = item.org_zip || item.zip;
            const location = [[city, state].filter(Boolean).join(", "), zip]
              .filter(Boolean)
              .join(" ");

            const lines = [name, location].filter(Boolean);
            if (lines.length) {
              return lines.join("\n");
            }
            const fallback = Object.values(item).find(
              (val) => typeof val === "string" && val.trim(),
            );
            return fallback ? fallback.trim() : null;
          }
          return null;
        })
        .filter(Boolean);
      return mapped;
    }
    if (typeof collection === "string" && collection.trim()) {
      return [collection.trim()];
    }
    return [];
  }, [userDetail]);

  const subscriptionDetail = useMemo(() => {
    const candidates = [];
    const pushEntry = (entry) => {
      if (!entry) return;
      if (Array.isArray(entry)) {
        entry.forEach(pushEntry);
        return;
      }
      if (typeof entry === "object") {
        candidates.push(entry);
      }
    };

    pushEntry(userDetail?.subscriptions);
    pushEntry(userDetail?.subscription);
    pushEntry(selectedUser?.subscriptions);
    pushEntry(selectedUser?.subscription);

    return (
      candidates.find((item) => {
        if (!item || typeof item !== "object") return false;
        return ["invoice_id", "customer_id", "customer_code"].some((key) => {
          const value = item[key];
          return value !== undefined && value !== null && value !== "";
        });
      }) || null
    );
  }, [userDetail, selectedUser]);

  const handleBecome = useCallback(() => {
    if (!selectedUser) return;

    if (subscriptionDetail) {
      const { invoice_id, customer_id, customer_code } = subscriptionDetail;
      dispatch(getSubscriptionDetail(invoice_id, customer_id, customer_code));
    }

    dispatch(setBecomeOrigin("directory"));
    dispatch(setBecome(1));
    dispatch(becomeUser(selectedUser.user_id));
    navigate("/myinfo");
  }, [dispatch, navigate, selectedUser, subscriptionDetail]);

  const handleEditMoreInfo = useCallback(() => {
    if (!selectedUser) return;
    navigate(`/admincentral/users/update/${selectedUser.user_id}`, {
      state: {
        selectedUser,
        userDetail,
      },
    });
  }, [navigate, selectedUser, userDetail]);

  const openStatusConfirm = useCallback(
    (type) => {
      if (!selectedUser || actionLoading) return;
      const isDeactivate = type === "deactivate";
      setConfirmAction({
        type,
        title: isDeactivate ? "Deactivate User" : "Activate User",
        message: isDeactivate
          ? "Are you sure you want to deactivate this user?"
          : "Are you sure you want to activate this user?",
      });
    },
    [selectedUser, actionLoading],
  );

  const executeStatusChange = useCallback(async () => {
    if (!confirmAction || !selectedUser || actionLoading) return;
    const isDeactivate = confirmAction.type === "deactivate";
    const nextFlag = isDeactivate ? "0" : "1";

    setActionLoading(true);
    try {
      if (isDeactivate) {
        await API.blockUser({ user_id: selectedUser.user_id });
      } else {
        await API.unblockUser({ user_id: selectedUser.user_id });
      }

      setUserDetail((prev) => {
        if (!prev) return prev;
        const updatedMembership = prev.membership
          ? { ...prev.membership, is_active: nextFlag }
          : prev.membership;
        return { ...prev, is_active: nextFlag, membership: updatedMembership };
      });
      setSelectedUser((prev) =>
        prev ? { ...prev, is_active: nextFlag } : prev,
      );
      setDetailError("");
      setConfirmAction(null);
    } catch (err) {
      setDetailError(
        err?.message ||
          (isDeactivate
            ? "Unable to deactivate user. Please try again."
            : "Unable to activate user. Please try again."),
      );
    } finally {
      setActionLoading(false);
    }
  }, [confirmAction, selectedUser, actionLoading]);

  if (selectedUser && showPasswordReset) {
    const membershipEmail = membership?.email || selectedUser.email;
    const firstName =
      membership?.first_name ||
      userDetail?.first_name ||
      selectedUser?.first_name ||
      "";
    const lastName =
      membership?.last_name ||
      userDetail?.last_name ||
      selectedUser?.last_name ||
      "";
    const fullName =
      [firstName, lastName].filter(Boolean).join(" ") ||
      selectedUser.full_name ||
      membershipEmail;

    return (
      <Container>
        <AdminResetPassword
          teacher={{
            user_id: selectedUser.user_id,
            email: membershipEmail,
            name: fullName,
          }}
          colors={colors}
          onCancel={() => setShowPasswordReset(false)}
          onComplete={() => setShowPasswordReset(false)}
        />
      </Container>
    );
  }

  if (selectedUser) {
    const detailRows = detailFieldConfig.map((config, index) => ({
      label: config.label,
      value: toDisplayValue(readDetailValue(config)),
      alt: index % 2 === 1,
    }));

    return (
      <>
        <Container>
          <DetailLayout>
            <ResponsiveActions
              colors={colors}
              renderActions={() => (
                <ActionsPanel colors={colors}>
                  <ActionsHeader colors={colors}>Actions</ActionsHeader>
                  <ActionsBody>
                    <ActionList>
                      <li>
                        <ActionButton
                          colors={colors}
                          onClick={handleNewSearchFromDetail}
                        >
                          New Search
                        </ActionButton>
                      </li>
                      <li>
                        <ActionButton
                          colors={colors}
                          onClick={handleEditMoreInfo}
                          disabled={detailLoading || actionLoading}
                        >
                          Edit & More Info
                        </ActionButton>
                      </li>
                      <li>
                        <ActionButton
                          colors={colors}
                          onClick={() =>
                            openStatusConfirm(
                              isActive ? "deactivate" : "activate",
                            )
                          }
                          disabled={detailLoading || actionLoading}
                        >
                          {isActive ? "Deactivate" : "Activate"}
                        </ActionButton>
                      </li>
                      <li>
                        <ActionButton
                          colors={colors}
                          onClick={handleBecome}
                          disabled={actionLoading}
                        >
                          Become
                        </ActionButton>
                      </li>
                    </ActionList>
                  </ActionsBody>
                </ActionsPanel>
              )}
            />

            <DetailPanel colors={colors}>
              <DetailSection>
                <SectionTitle colors={colors}>
                  Personal Information
                </SectionTitle>
                {detailLoading ? (
                  <DetailMessage colors={colors}>
                    Loading user detail…
                  </DetailMessage>
                ) : (
                  <>
                    {detailError ? (
                      <DetailMessage colors={colors}>
                        {detailError}
                      </DetailMessage>
                    ) : null}
                    <DetailRowsWrapper colors={colors}>
                      {detailRows.map((row) => (
                        <DetailRow
                          key={row.label}
                          colors={colors}
                          $alt={row.alt}
                        >
                          <DetailLabel colors={colors}>{row.label}</DetailLabel>
                          <DetailValue colors={colors}>
                            <span className="value-text">{row.value}</span>
                            {row.label === "Password" && (
                              <div className="password-actions">
                                <ChangePasswordButton
                                  type="button"
                                  colors={colors}
                                  onClick={() => setShowPasswordReset(true)}
                                >
                                  Change Password
                                </ChangePasswordButton>
                              </div>
                            )}
                          </DetailValue>
                        </DetailRow>
                      ))}
                    </DetailRowsWrapper>
                  </>
                )}
              </DetailSection>

              <DetailSection>
                <SectionTitle colors={colors}>Affiliations</SectionTitle>
                {detailLoading ? (
                  <DetailMessage colors={colors}>Loading…</DetailMessage>
                ) : affiliations.length ? (
                  <AffiliationsList>
                    {affiliations.map((item, index) => (
                      <AffiliationsItem
                        key={`${item}-${index}`}
                        colors={colors}
                      >
                        {item}
                      </AffiliationsItem>
                    ))}
                  </AffiliationsList>
                ) : (
                  <DetailMessage colors={colors}>
                    No affiliations found.
                  </DetailMessage>
                )}
              </DetailSection>
            </DetailPanel>
          </DetailLayout>
        </Container>
        <ConfirmationModal
          show={Boolean(confirmAction)}
          title={confirmAction?.title}
          message={confirmAction?.message}
          onCancel={() => {
            if (actionLoading) return;
            setConfirmAction(null);
          }}
          onConfirm={executeStatusChange}
        />
      </>
    );
  }

  // render advanced form
  if (view === "advanced") {
    return (
      <Container>
        <h1 style={{ color: colors.labelText }}>User Search</h1>
        <AdvancedForm colors={colors} onSubmit={handleAdvancedSubmit}>
          {[
            ["First Name", firstName, setFirstName],
            ["Last Name", lastName, setLastName],
            ["Email", advEmail, setAdvEmail],
            ["City", city, setCity],
            ["Zip", zip, setZip],
            ["Phone", phone, setPhone],
          ].map(([label, val, setter]) => (
            <div className="input" key={label}>
              <label>{label}</label>
              <input value={val} onChange={(e) => setter(e.target.value)} />
            </div>
          ))}
          <div className="submit">
            <input type="submit" value="Search" />
            <CancelLink colors={colors} onClick={() => setView("list")}>
              Cancel
            </CancelLink>
          </div>
        </AdvancedForm>
      </Container>
    );
  }

  // list view
  const start = page * itemsPerPage + 1;
  const end =
    itemsPerPage === -1 ? total : Math.min((page + 1) * itemsPerPage, total);

  return (
    <Container>
      <HeaderWrapper>
        {advApplied ? (
          <HeaderButton colors={colors} onClick={handleReset}>
            Reset Filters
          </HeaderButton>
        ) : (
          <HeaderButton colors={colors} onClick={() => setView("advanced")}>
            Advanced Search
          </HeaderButton>
        )}
      </HeaderWrapper>

      <ControlsContainer colors={colors}>
        <div>
          <label>Show</label>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setPage(0);
            }}
          >
            {[10, 25, 50, 100, -1].map((n) => (
              <option key={n} value={n}>
                {n === -1 ? "All" : n}
              </option>
            ))}
          </select>
          <label style={{ marginLeft: "5px" }}>entries</label>
        </div>
        <div>
          <label>Search:</label>
          <input
            type="text"
            placeholder="Type to filter..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            disabled={advApplied}
          />
        </div>
      </ControlsContainer>

      {loading ? (
        <p>Loading…</p>
      ) : (
        <>
          <TableWrapper>
            <Table colors={colors}>
              <thead>
                <tr>
                  {[
                    ["name", "Name"],
                    ["email", "Email"],
                    ["street", "Street"],
                    ["city", "City"],
                    ["state", "State"],
                    ["zip", "Zip"],
                    ["phone", "Phone"],
                  ].map(([key, label]) => {
                    const isAscActive = orderBy === key && order === "ASC";
                    const isDescActive = orderBy === key && order === "DESC";
                    const defaultColor = "#9CA3AF";
                    const activeColor = "#A855F7";
                    return (
                      <th key={key}>
                        {label}
                        <div className="sort-icons">
                          <span
                            onClick={() => handleSort(key)}
                            className={isAscActive ? "active" : ""}
                            style={{
                              color: isAscActive ? activeColor : defaultColor,
                              display: isDescActive ? "none" : "block",
                            }}
                          >
                            ▲
                          </span>
                          <span
                            onClick={() => handleSort(key)}
                            className={isDescActive ? "active" : ""}
                            style={{
                              color: isDescActive ? activeColor : defaultColor,
                              display: isAscActive ? "none" : "block",
                            }}
                          >
                            ▼
                          </span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rows?.map((u) => {
                  // determine display name
                  const name =
                    u.full_name?.trim() ||
                    `${u.first_name || ""} ${u.last_name || ""}`.trim();
                  return (
                    <tr key={u.user_id}>
                      <td>
                        <NameButton
                          type="button"
                          colors={colors}
                          onClick={() => handleSelectUser(u)}
                        >
                          {name || "--"}
                        </NameButton>
                      </td>
                      <td>{u.email}</td>
                      <td>{u.street || ""}</td>
                      <td>{u.city || ""}</td>
                      <td>{u.state || ""}</td>
                      <td>{u.zip || ""}</td>
                      <td>{u.phone || ""}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrapper>

          <InfoText colors={colors}>
            Showing {total > 0 ? start : 0} to {end} of {total} entries
          </InfoText>
          <Pagination colors={colors}>
            <button
              onClick={() => setPage((p) => Math.max(p - 1, 0))}
              disabled={page === 0}
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={itemsPerPage === -1 || end === total}
            >
              Next
            </button>
          </Pagination>
        </>
      )}
    </Container>
  );
}
